(ns dre.widgets.authors.events
  (:require
   [clojure.string :as str]
   [day8.re-frame.tracing :refer-macros [fn-traced]]
   [dre.components.flash.events :as-alias flash]
   [dre.i18n.views :refer [tr]]
   [dre.panels.events :as-alias panels]
   [dre.network :as-alias network]
   [dre.pages.search.events :as search-events]
   [dre.page-util :refer [round]]
   [dre.utils.query :as query]
   [dre.utils :refer [stringify-keys-with-namespaces aborted-request?]]
   [dre.shared.interface.search.pills :as pills]
   [re-frame.core :as rf]
   [superstructor.re-frame.fetch-fx :as-alias fetch-fx]))


(def db-path [:widgets :authors])

(def id
  :authors)

(defn api-params
  [pills-api-params]
  (let [k 25
        graph? true]
    (merge pills-api-params
           {:response/filter
            {:done? true
             :topk true
             :graph true}
            :k k
            :graph? graph?})))

(defn all-uppercase? [s]
  (= s (str/upper-case s)))

(def affixes
  ;; https://en.wikipedia.org/wiki/List_of_family_name_affixes
  #{"zu" "stor"
    "el" "da"  "fetch" "vetch"
    "ab" "ap" "al" "alam" "bet"
    "bint" "del" "dele" "dos"
    "fitz" "kil" "mala" "na" "neder"
    "ni" "nic" "nin"
    "nord" "norr"
    "ny" "au" "ui" "ost"
    "ofver" "over" "pour" "ter"
    "tre" "vast" "vaster" "woj"
    "de" "le" "van"
    "van der" "van de" "de la"
    "la" "von" "var" "am" "zum"})

(defn capitalize-name
  [s]
  (if (and
       (> (count s) 3)
       (all-uppercase? s))
    (str/capitalize s)
    (if (affixes (str/lower-case s))
      (str/lower-case s) s)))

(defn author-capitalize
  [name]
  (let [{:keys [:author/given-names :author/surnames]} name]
    (str/join " " (map capitalize-name (concat given-names surnames)))))

(defn linear-scale
  [min max v]
  (/ (- v min) (- max min)))

(defn tanh
  [x]
  (let [y (js/Math.exp (* 2 x))]
    (/ (- y 1) (+ y 1))))

(defn circle-size
  [min-size max-size max-score]
  (fn [v]
    (+ min-size (* (- max-size min-size) (tanh (/ v max-score))))))

(defn ->d3-graph
  [graph topk]
  (let [{:keys [vertices edges nodes]} graph
        v (into {} (map-indexed (fn [idx itm] [itm idx]) vertices))
        ;; score is :group/normalized-score which is 0..1
        ;; topk entries contains :group/normalized-score, nodes does not
        score-by-author-lookup (into {}
                                     (map (fn [node]
                                            (let [author (:author/author node)]
                                              [(:author/lookup author)
                                               (:group/normalized-score node)])))
                                     topk)
        max-score (apply max (vals score-by-author-lookup))
        size-fn (circle-size 2 15 max-score)
        data-fn (fn [v]
                  (let [n (get nodes (keyword (str v)))
                        author (:author/author n)
                        score (get score-by-author-lookup (:author/lookup (:author/author n)))
                        name (get-in n [:author/author :author/name])]
                    {:score score
                     :author author
                     :size (size-fn score)
                     :name (author-capitalize name)}))]
    {:links (map (fn [e] {:target (get v (second e)) :source (get v (first e))}) edges)
     :nodes (sort-by :index (map (fn [[v idx]] (merge (data-fn v) {:index idx})) v))}))


(defn entries-by-normalized-score
  [entries]
  ;; entries has topk entries, followed by the fringe authors in the graph
  ;; the fringe authors get a pagerank assigned, scaled to the lowest pagerank from the topk authors, based on their :group/score
  ;; also adds a divider entry between topk author and the fringe authors
  (let [min-score 0
        max-score (apply max (map :pagerank/score entries))
        normalize-score (partial linear-scale min-score max-score)

        [entries _scale-missing-pagerank]
        (reduce
         (fn [[out scale-missing-pagerank] entry]
           (let [divider-point (and (nil? (:pagerank/score entry))
                                    (nil? scale-missing-pagerank))
                 scale-missing-pagerank
                 (if-not divider-point
                   scale-missing-pagerank
                   (let [lowest-seen-score (-> (rseq out)
                                               first
                                               :group/normalized-score)]
                     (fn [other]
                       (* lowest-seen-score
                          0.45 ;; make none topk authors rank much lower
                          (/ (:group/score other)
                             (:group/score entry))))))
                 e (if (:pagerank/score entry)
                     (assoc entry :group/normalized-score
                            (normalize-score (:pagerank/score entry)))
                     (assoc entry
                            :group/normalized-score
                            (scale-missing-pagerank entry)))
                 divider (when divider-point
                           {:divider :split-topk-fringe
                            ;; keep it at the correct point when sorting
                            :group/normalized-score (:group/normalized-score e)})]
             [(cond-> out
                divider
                (conj divider)
                true
                (conj e))
              scale-missing-pagerank]))
         [[] nil]
         entries)]
    (sort-by #(* -1 (:group/normalized-score %)) entries)))

(defn ->pill
  [author n]
  (pills/author-pill
   (or (:author/lookup author) (get author "lookup"))
   n))

(defn ->pill-with-index
  [author n index]
  (assoc (pills/author-pill
          (or (:author/lookup author) (get author "lookup"))
          n) :index index))

;; -- Events

(rf/reg-event-fx
 ::set-loading
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} _]
  {:db (assoc db :state :loading)}))

(rf/reg-event-fx
 ::set-show-author-search?
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [show?]]
  {:db (assoc db :show-author-search? show?)}))

(rf/reg-event-fx
 ::init
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx last-selected-filter-key pills-api-params-fn attempt]}]]
  (if (= last-selected-filter-key id) 
   {:db (assoc db :state nil)
    :dispatch after-fx}
    (let [author-search-string (:author-input-text db)
        params
        (-> (pills-api-params-fn [id])
            api-params)
        params (if (and author-search-string (not (empty? author-search-string)))
                 (update
                  params :query/pill
                  (fn [pill]
                    {:pill/type :authors-search
                     :pill/query author-search-string
                     :pill/pill pill}))
                 params)]
    {:db {:state :loading
          :show-author-search? (or (get db :show-author-search?) false)
          :author-input-text author-search-string}
     :dispatch-later {:ms (+ 300 (min 6000 (* 300 (or attempt 0))))
                      :dispatch [::network/request-with-abort {:endpoint :widgets/authors}
                                 {:body (stringify-keys-with-namespaces params)
                                  :on-success [::success {:after-fx after-fx :pills-api-params-fn pills-api-params-fn :attempt attempt}]
                                  :on-failure [::failure {:after-fx after-fx}]}]}}))))

(rf/reg-event-fx
 ::search-author
 [(rf/path search-events/db-path) rf/trim-v]
 (fn-traced
  [{db :db} [_]]
  (let [search-query (:search-query db)
        widget-filters (:widget-filters db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)]
    {:dispatch [::init {:pills-api-params-fn aq*}]})))

(rf/reg-event-fx
 ::set-author-input-text
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [author-search-string search?]]
  (if search?
    {:db (assoc db :author-input-text author-search-string)
     :dispatch [::search-author]}
    {:db (assoc db :author-input-text author-search-string)})))

(rf/reg-event-fx
 ::success
 [(rf/path db-path) rf/trim-v]
 (fn
  [{db :db} [{:keys [after-fx pills-api-params-fn attempt ::fetch-fx/response]}]]
  (let [done? (:done? (:body response))
        data (:body response)
        fx (if (and (not done?) (> 10 attempt))
             [::init {:after-fx after-fx :pills-api-params-fn pills-api-params-fn :attempt (inc attempt)}]
             after-fx)] 
   {:db {:state :success
         :author-input-text (:author-input-text db)
         :show-author-search? (or (get db :show-author-search?) false)
         :data (if done?
                 data
                 nil)
         :done? done?}
   :dispatch fx})))


(rf/reg-event-fx
 ::failure
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [after-fx ::fetch-fx/response] :as opts}]]
  (if (aborted-request? response)
    {}
    {:db {:state :failure
          :show-author-search? (or (get db :show-author-search?) false)
          :data nil}
     :dispatch-n [after-fx
                  [:dre.network/default-failure (merge {:event :authors} opts)]]})))

(rf/reg-event-fx
 ::success-pagerank-authors-export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [pills-api-params attempt ::fetch-fx/response]}]]
  (let [done? (:done? (:body response))
        data (:message (:body response))
        fx (cond
             (and (not done?) (> 30 attempt))
             [::do-pagerank-authors-export {:pills-api-params pills-api-params :attempt (inc attempt)}]
             (not done?)
             [::flash/show
              [:span [:b [tr "Error during Influential Authors report preparation, try it again later."] [:br]]]
              :severity :error]
             :else
             [::flash/show
              [:span [:b {:dangerouslySetInnerHTML {:__html data} }]]
              :severity :info])]
    {:dispatch fx})))

(rf/reg-event-fx
 ::do-pagerank-authors-export
 [(rf/path db-path) rf/trim-v]
 (fn-traced
  [{db :db} [{:keys [pills-api-params attempt]}]]
  (let [author-search-string (:author-input-text db)
        params pills-api-params
        params (if (and author-search-string (not (empty? author-search-string)))
                 (update
                  params :query/pill
                  (fn [pill]
                    {:pill/type :authors-search
                     :pill/query author-search-string
                     :pill/pill pill}))
                 params)]
    {:dispatch-later {:ms 5000
                      :dispatch [::network/request-with-abort {:endpoint :widgets/authors-export}
                                 {:body (stringify-keys-with-namespaces params)
                                  :on-success [::success-pagerank-authors-export {:pills-api-params pills-api-params :attempt attempt}]}]}})))

(rf/reg-event-fx
 ::export
 [(rf/path search-events/db-path) rf/trim-v]
 (fn-traced
  [{db :db} [export-count]]
  (let [search-query (:search-query db)
        widget-filters (:widget-filters db)
        aq (partial search-events/analysis-query search-query widget-filters)
        aq* (comp second aq)
        count (if export-count 
                (js/parseInt (name export-count))
                25)
        pills-api-params (aq* [id])
        pills-api-params (assoc pills-api-params :k count)]
    {:dispatch-n [[::flash/show
                   [:span [:b [tr "Your Influential Authors report is being prepared in the background."] [:br]]]
                   :severity :info]
                  [::do-pagerank-authors-export {:pills-api-params pills-api-params :attempt 0}]]})))

;; -- Subscriptions

(rf/reg-sub
 ::state
 (fn [db _]
   (get-in db db-path)))

(rf/reg-sub
 ::status
 :<- [::state]
 (fn [db _]
   (:state db)))

(rf/reg-sub
 ::author-input-text
 :<- [::state]
 (fn [db _]
   (get db :author-input-text)))

(rf/reg-sub
 ::show-author-search?
 :<- [::state]
 (fn [db _]
   (get db :show-author-search?)))

(rf/reg-sub
 ::done?
 :<- [::state]
 (fn [db _]
   (:done? db)))


(rf/reg-sub
 ::data
 :<- [::state]
 :<- [::search-events/widget-filters]
 :<- [::search-events/filtered-query]
 (fn [[db filters search-query] _]
   (let [filter-key id
         f (get filters filter-key)
         data (:data db)
         entries (:topk data)
         entries (entries-by-normalized-score entries)
         on-click (fn [pill _name]
                    (rf/dispatch [::search-events/widget-filter-select filter-key pill]))]
     (map-indexed
      (fn [idx entry]
        (let [{:keys [:author/author
                      :group/normalized-score]} entry
              {:keys [:author/name :author/lookup]} author
              n (author-capitalize name)
              pill (->pill author n)
              f? (contains? f pill)]
          (if (:divider entry)
            {:label [tr "Connecting authors"]
             :term [:span.separator "divider"]
             :idx idx
             :asis true
             :style {:font-weight "bold"}}
            (merge
             entry
             {:score (round (* normalized-score 100))
              :label n
              :idx idx
              :open-fn #(rf/dispatch [::panels/set-panel :author {:params entry :search-query search-query}])
              :class [(when (seq f) (if f? "active" "inactive"))]
              :on-click (fn [_] (on-click pill n))
              :term lookup}))))
      entries))))

(rf/reg-sub
 ::data-graph
 :<- [::state]
 :<- [::data]
 (fn [[db entries] _]
   (let [data (:data db)]
     (->d3-graph (:graph data) entries))))

;; 
(defn- authors-pill?
  [{:pill/keys [type]}]
  (= type :author))

(defn authors-filters
  [db]
  (->> db
       search-events/db-path
       :widget-filters
       id
       (filter authors-pill?)))

(defn authors-queries
  [db]
  (->> db
       search-events/db-path
       :search-query
       :query/pills
       (filter authors-pill?)))

(defn authors-source?
  [db]
  (let [x (concat (authors-filters db) (authors-queries db))]
    (not-empty x)))

(rf/reg-sub
 ::used-in-search?
 authors-source?)